import React, { useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import {
  CardViewDataStateChangeEvent,
  OrganisationsCardView
} from './organisations-cardview';
import { useNavigate } from 'react-router-dom';
import {
  DeleteOrganisationAsync,
  GetReportOrganisationsAsAdminAsync,
  GetUsersOnOrganisationCheckAsync
} from '../services/organisations';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { ColumnMenu } from '../components/columnMenu';
import Toolbar from '../components/page-toolbar';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { Button } from '@progress/kendo-react-buttons';
import { NoRecords } from '../components/grid/no-records';
import { DefaultGridSettings } from '../constants/grid-settings';
import { ActionCell } from '../components/grid/action-cell';
import { ActionsCell } from '../components/grid/actions-cell';
import { YesNoDialogResult } from '../types/dialog-result';
import YesNoDialog from '../components/dialogs/yes-no-dialog';
import { appStore } from '../stores/app-store';
import { useToast } from '../contexts/toast-context';
import { useGetAllOrganisationsAdmin } from '../hooks/services/use-organisarions';
import { OrganisationRequest } from '../types/organisation';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'name', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const OrganisationsGrid = () => {
  const navigate = useNavigate();

  const [dataState, setDataState] = useState<State>(initialDataState);
  const [gridMode, setGridMode] = useState<string>('grid');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(0);

  const { onToastOpen } = useToast();

  const { data, isLoading, mutate } = useGetAllOrganisationsAdmin(
    toDataSourceRequestString(dataState)
  );

  const onDelete = (id: number) => {
    handleDeleteOrganisation(id);
  };

  const handleDeleteOrganisation = (id: number) => {
    setSelectedId(id);
    setShowDeleteConfirm(true);
  };

  const onConfirmDelete = async (result: YesNoDialogResult) => {
    if (result === 'yes') {
      try {
        appStore.showLoading();

        const isAvailable = await GetUsersOnOrganisationCheckAsync(selectedId);
        if (isAvailable) {
          onToastOpen({
            title: 'Organisation',
            body: (
              <div>
                Failed to delete organisation. Delete all members first.
              </div>
            ),
            style: 'error'
          });
        } else {
          await DeleteOrganisationAsync(selectedId);
          // fetchOrganisations();
          mutate();

          onToastOpen({
            title: 'Organisation',
            body: <div>Organisation deleted.</div>,
            style: 'success'
          });
        }
      } catch {
        onToastOpen({
          title: 'Organisation',
          body: <div>Failed to delete oranisation.</div>,
          style: 'error'
        });
      } finally {
        appStore.hideLoading();
      }
    }
    setShowDeleteConfirm(false);
  };

  const closeDeleteDialog = () => {
    setShowDeleteConfirm(false);
  };
  //edit organisation

  const editEventById = (id: number) => {
    mutate();
  };

  const onEditEvent = (id: number) => {
    navigate(`../organisations/${id.toString()}`);

    editEventById(id);
  };

  const onActionRow = (props: GridCellProps) => {
    return (
      <>
        <ActionsCell {...props}>
          <ActionCell
            title={'View organisation'}
            icon={'visibility'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => navigateToOrganisationDetail(props.dataItem.id)}
          />
          <ActionCell
            title={'Edit organisation'}
            icon={'edit'}
            themeColor={'primary'}
            className={'fs-2'}
            onClick={() => onEditEvent(props.dataItem.id)}
          />
          <ActionCell
            title={'Delete organisation'}
            icon={'delete'}
            themeColor={'error'}
            className={'fs-2'}
            onClick={() => onDelete(props.dataItem.id)}
          />
        </ActionsCell>
      </>
    );
  };

  const StatusColoring = (props: GridCellProps) => {
    const status = props.dataItem.status;
    return (
      <td
        className={`k-table-td ${status === 'Active' ? 'text-success' : 'text-muted'}`}>
        <span>{status}</span>
      </td>
    );
  };

  const onAddUser = () => {
    navigate('new');
  };

  const EditCommandCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        <span
          className={'link-primary cursor-pointer'}
          onClick={() => handleNameClick(props.dataItem.id, 'grid')}>
          {props.dataItem.name}
        </span>
      </td>
    );
  };

  const WebsiteColumn = (props: GridCellProps) => (
    <td className={'k-table-td'}>
      <a
        href={`https://${props.dataItem.website}`}
        target={'_blank'}
        className={'link-primary cursor-pointer'}>
        {props.dataItem.website}
      </a>
    </td>
  );

  const handleNameClick = (id: number, viewSource?: string) => {
    navigate('/administration/organisations/' + id + '?view=' + viewSource);
  };

  const navigateToOrganisationDetail = (id: number) => {
    navigate('/administration/organisation-details/' + id);
  };

  const onExportExcel = async () => {
    appStore.showLoading();
    try {
      await GetReportOrganisationsAsAdminAsync().then((response) => {
        if (gridExportRef.current !== null) {
          gridExportRef.current.save(response);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      appStore.hideLoading();
    }
  };

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const TruncatedCellWithTooltip = (props: any) => {
    const { dataItem, field } = props;
    const cellValue = dataItem[field];

    return (
      <td
        title={cellValue}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '150px'
        }}>
        {cellValue}
      </td>
    );
  };

  return (
    <>
      <Toolbar title={'Organisations'}>
        <Button onClick={onAddUser} themeColor={'primary'}>
          Add new
        </Button>
      </Toolbar>
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search organisations'}
          columnsToSearch={['name']}
          exportRef={gridExportRef}
          useCustomExportExcel={true}
          handleCustomExportToExcel={onExportExcel}
          onGridModeChange={(e) => {
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport ref={gridExportRef} fileName={`organisations.xlsx`}>
          <ExcelExportColumn field="name" title="Organisation" width={270} />
          <ExcelExportColumn
            field="territory"
            title="Territory(S)"
            width={270}
          />
          <ExcelExportColumn field="email" title="Email" width={200} />
          <ExcelExportColumn field="phoneNumber" title="Phone" width={200} />
          <ExcelExportColumn field="admins" title="Admin" width={350} />
          <ExcelExportColumn field="languages" title="Language" width={250} />
          <ExcelExportColumn field={'website'} title={'Website'} width={250} />
          <ExcelExportColumn field={'status'} title={'Status'} width={250} />
        </ExcelExport>
        {gridMode === 'grid' && (
          <Grid
            id={'grid-organisations'}
            style={{
              flexGrow: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'hidden'
            }}
            pageable={DefaultGridSettings.pagerSettings}
            sortable={true}
            data={data}
            {...dataState}
            onDataStateChange={(e: GridDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}>
            <Column
              field={'name'}
              title={'Organisation'}
              cells={{ data: EditCommandCell }}
              columnMenu={ColumnMenu}
            />
            <Column field={'territory'} title={'Territory(S)'} />
            <Column field={'email'} title={'Email'} />
            <Column field={'phoneNumber'} title={'Phone'} />
            <Column
              field={'admins'}
              title={'Admin'}
              cells={{ data: TruncatedCellWithTooltip }}
            />
            <Column field={'languages'} title={'Language'} />
            <Column
              field={'website'}
              title={'Website'}
              cells={{ data: WebsiteColumn }}
            />
            <Column
              field={'status'}
              title={'Status'}
              columnMenu={ColumnMenu}
              cells={{ data: StatusColoring }}
              width={100}
            />
            <Column
              field={'actions'}
              title={'Actions'}
              cells={{ data: onActionRow }}
              width={160}
            />

            <GridNoRecords>
              {isLoading ? (
                <div
                  className={
                    'd-flex flex-column align-items-center gap-2 text-muted'
                  }>
                  <Loader type={'pulsing'} />
                  <span>Loading data</span>
                </div>
              ) : (
                <NoRecords />
              )}
            </GridNoRecords>
          </Grid>
        )}
        {gridMode === 'card' && (
          <OrganisationsCardView
            data={(data?.data ?? []) as OrganisationRequest[]}
            onNameClick={handleNameClick}
            total={data?.total}
            {...dataState}
            onDataStateChange={(e: CardViewDataStateChangeEvent) => {
              setDataState(e.dataState);
            }}
          />
        )}

        {showDeleteConfirm && (
          <YesNoDialog
            title={'Remove Organisation'}
            onConfirm={onConfirmDelete}
            onClose={closeDeleteDialog}>
            Are you sure you want to delete this organisation?
          </YesNoDialog>
        )}
      </div>
    </>
  );
};
export default OrganisationsGrid;
